import { RichText } from '@almbrand/richtext';
import classNames from 'classnames';
import { ConsentModal } from 'components';
import { useFormSubmit, usePageParam } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormResetField, UseFormTrigger } from 'react-hook-form';
import { apiInstanceCached } from '../../../services/Api';
import { replacePlaceholders } from '../../../services/PlaceholderService';
import styles from './CheckboxConsentComponent.module.scss';

// checkBox copied from abg-component-library v. 1.0.18

export interface CheckboxConsentComponentProps extends React.HTMLAttributes<HTMLInputElement> {
	name: string;
	preText?: string;
	linkText?: string;
	titleConsentText?: string;
	subtitleConsentText?: string;
	consentText?: string;
	placeholders?: Array<{ key: string; value: string }>;
	endpoint?: string;
	register?: UseFormRegister<FieldValues>;
	resetField?: UseFormResetField<FieldValues>;
	submitStep?;
	contentType?: string;
	pageId?: string;
	errorMessage?: string;
	hasError?: boolean;
	trigger: UseFormTrigger<FieldValues>;
	stepTitle?: string;
}

export const CheckboxConsentComponent: React.FC<CheckboxConsentComponentProps> = ({
	name,
	preText,
	linkText,
	titleConsentText,
	subtitleConsentText,
	consentText,
	placeholders,
	endpoint,
	register,
	resetField,
	submitStep,
	contentType,
	pageId,
	errorMessage,
	hasError,
	trigger,
	stepTitle,
}) => {
	const [consentPreText, setConsentPreText] = useState<string>(undefined);
	const [consentPostText, setConsentPostText] = useState<string>(undefined);
	const { onChange } = register(name, { required: true });
	const [hasValidateError, setHasValidateError] = useState(hasError);
	const { resetFormField } = useFormSubmit();
	const input = useRef(null);
	const { getPageParam } = usePageParam();

	const init = async () => {
		if (getPageParam() === pageId) {
			let res;
			try {
				res = await apiInstanceCached().get(endpoint);
			} catch (e) {
				// move on
			}
			const consentApi = res?.data?.find(
				(consent) => consent.developerName === 'onlinetermination_private' && consent.consentText?.length > 0
			);
			replaceTokens(consentApi?.consentText ?? consentText);
		}
	};

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.checked) {
			resetField(name);
			resetFormField(name);
			trigger(name);
			return;
		}
		setHasValidateError(false);
		onChange({
			target: {
				name,
				value: {
					id: name,
					value: 'Y',
					label: 'ja',
					inputLabel: name,
					type: contentType,
					stepTitle,
				},
			},
		});
		submitStep?.();
	};

	const replaceTokens = (consentText: string) => {
		for (const entry of placeholders || []) {
			preText = preText.replaceAll(entry.key, entry.value);
			consentText = consentText.replaceAll(entry.key, entry.value);
		}
		const field: FieldsBlock = { contentType: 'UserConsentBlock', content: preText };
		replacePlaceholders(field);
		setConsentPreText(field.content);

		field.content = consentText;
		replacePlaceholders(field);
		setConsentPostText(field.content);
	};

	useEffect(() => {
		setHasValidateError(hasError);
	}, [hasError]);

	useEffect(() => {
		init();
	}, [getPageParam()]);

	return (
		<label className={styles.Checkbox}>
			{consentPreText && (
				<div className={classNames(styles.ContentWrapper, styles.Checkbox__richText)}>
					<RichText description={consentPreText} />
					{consentPostText && (
						<ConsentModal
							linkText={linkText}
							titleConsentText={titleConsentText}
							subtitleConsentText={subtitleConsentText}
							toggleContentText={consentPostText}
						/>
					)}
					{hasValidateError && errorMessage && (
						<p
							role='alert'
							className={styles.ContentWrapper__error}
						>
							{errorMessage}
						</p>
					)}
				</div>
			)}

			<input
				onChange={handleOnChange}
				name={name}
				className={styles.Checkbox_input}
				type='checkbox'
				ref={input}
			/>
			<span className={styles.Checkbox_checkmark} />
		</label>
	);
};
